import {
  ApiError,
  createPayeeSchema,
  formatMoney,
  MANUAL_PAYMENT_METHODS,
  OUTGOING_PAYMENT_KIND_LABELS,
  OUTGOING_PAYMENT_KINDS,
  OUTGOING_PAYMENT_METHOD_LABELS,
  type OutgoingPayment,
  type OutgoingPaymentKind,
  type OutgoingPaymentMethod,
  type OutgoingPaymentStatus,
  type OutgoingPaymentSummary,
  type Payee,
  queryKeys,
  type RecurringExpense,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Paperclip, Pencil, Plus, Search, Wallet } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { InfiniteScrollFooter } from '../components/data-table.js'
import { RecordPaymentDialog, usePaymentLookups } from '../components/record-payment-dialog.js'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const STATUS_TONE: Record<string, 'warning' | 'success' | 'neutral'> = {
  unpaid: 'warning',
  paid: 'success',
  void: 'neutral',
}

const ALL = 'all'
const th = 'px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted'

const iso = (d: Date) => d.toLocaleDateString('en-CA')
type Period = 'this-month' | 'last-month' | 'this-year' | 'all'

function periodRange(period: Period): { from?: string; to?: string } {
  const now = new Date()
  if (period === 'this-month') {
    return {
      from: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    }
  }
  if (period === 'last-month') {
    return {
      from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: iso(new Date(now.getFullYear(), now.getMonth(), 0)),
    }
  }
  if (period === 'this-year') {
    return {
      from: iso(new Date(now.getFullYear(), 0, 1)),
      to: iso(new Date(now.getFullYear(), 11, 31)),
    }
  }
  return {}
}

interface Person {
  kind: OutgoingPaymentKind
  id: string
  label: string
}

export function PaymentsPage() {
  const { can } = useAuth()
  const canManage = can('expenses:manage')
  const [tab, setTab] = useState('payments')
  const [period, setPeriod] = useState<Period>('this-month')
  const [person, setPerson] = useState<Person | null>(null)
  const [recording, setRecording] = useState(false)
  const range = periodRange(period)

  const { data: summary } = useQuery({
    queryKey: queryKeys.expenses.summary({ ...range }),
    queryFn: () => api.get<OutgoingPaymentSummary>('/outgoing-payments/summary', { query: range }),
  })

  return (
    <Page>
      <PageHeader
        title="Payments & expenses"
        description="What you pay vendors, staff and freelancers, plus rent, printing and other running costs. The app records the payment — the money itself moves at your bank."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setRecording(true)}>
              <Plus /> Record payment
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Period">
          {(props) => (
            <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <SelectTrigger {...props} className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this-month">This month</SelectItem>
                <SelectItem value="last-month">Last month</SelectItem>
                <SelectItem value="this-year">This year</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <div className="grid flex-1 grid-cols-2 gap-3 sm:max-w-md">
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                Paid
              </p>
              <p className="tabular text-xl font-semibold">
                {formatMoney(summary?.paidCents ?? 0)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                Still to pay
              </p>
              <p className="tabular text-xl font-semibold">
                {formatMoney(summary?.outstandingCents ?? 0)}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="recurring">Recurring</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>
        <TabsContent value="payments">
          <PaymentsTab
            range={range}
            person={person}
            onClearPerson={() => setPerson(null)}
            canManage={canManage}
          />
        </TabsContent>
        <TabsContent value="people">
          <PeopleTab
            summary={summary}
            canManage={canManage}
            onHistory={(p) => {
              setPerson(p)
              setTab('payments')
            }}
          />
        </TabsContent>
        <TabsContent value="recurring">
          <RecurringTab canManage={canManage} />
        </TabsContent>
        <TabsContent value="categories">
          <CategoriesTab canManage={canManage} />
        </TabsContent>
      </Tabs>

      <RecordPaymentDialog open={recording} onClose={() => setRecording(false)} />
    </Page>
  )
}

// ----------------------------------------------------------------- payments

function PaymentsTab({
  range,
  person,
  onClearPerson,
  canManage,
}: {
  range: { from?: string; to?: string }
  person: Person | null
  onClearPerson: () => void
  canManage: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<string>(ALL)
  const [status, setStatus] = useState<string>(ALL)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<OutgoingPayment | null>(null)
  const [paying, setPaying] = useState<OutgoingPayment | null>(null)
  const [voiding, setVoiding] = useState<OutgoingPayment | null>(null)
  const [attachFor, setAttachFor] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const filters = {
    q: search || undefined,
    kind: kind === ALL ? undefined : kind,
    status: status === ALL ? undefined : status,
    staffUserId: person?.kind === 'staff' ? person.id : undefined,
    payeeId: person?.kind === 'payee' ? person.id : undefined,
    vendorId: person?.kind === 'vendor' ? person.id : undefined,
    ...range,
    pageSize: 50,
  }
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.expenses.payments(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: OutgoingPayment[]; total: number; page: number; pageSize: number }>(
        '/outgoing-payments',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const items = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const attach = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const form = new FormData()
      form.append('file', file)
      return api.postFile(`/outgoing-payments/${id}/document`, form)
    },
    onSuccess: () => {
      toast.success('Receipt attached')
      void queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() })
    },
    onError: (error) =>
      toast.error(
        'Could not attach the receipt',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Type">
          {(props) => (
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger {...props} className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {OUTGOING_PAYMENT_KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {OUTGOING_PAYMENT_KIND_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Status">
          {(props) => (
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger {...props} className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {(['unpaid', 'paid', 'void'] as OutgoingPaymentStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {humanizeStatus(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" />
          <Input
            className="ps-8"
            placeholder="Search payments…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {person ? (
          <Button variant="secondary" size="sm" onClick={onClearPerson}>
            Showing {person.label} — clear
          </Button>
        ) : null}
      </div>

      <input
        ref={fileRef}
        type="file"
        hidden
        accept="application/pdf,image/jpeg,image/png,image/webp"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file && attachFor) attach.mutate({ id: attachFor, file })
          e.target.value = ''
        }}
      />

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className={th}>Date</th>
                <th className={th}>Paid to</th>
                <th className={th}>For</th>
                <th className={th}>Method</th>
                <th className={`${th} text-end`}>Amount</th>
                <th className={th}>Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : items.length > 0 ? (
                items.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 whitespace-nowrap tabular">
                      {p.paidOn ?? (p.dueOn ? `Due ${p.dueOn}` : p.createdAt.slice(0, 10))}
                      <div className="text-2xs text-foreground-muted">{p.number}</div>
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {p.recipient}
                      <div className="text-2xs font-normal text-foreground-muted">
                        {OUTGOING_PAYMENT_KIND_LABELS[p.kind]}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {p.categoryName ?? p.purchaseOrderNumber ?? p.invoiceRef ?? '—'}
                      {p.invoicePurchaseOrderNumber ? (
                        <div className="text-2xs">
                          Office supplies on {p.invoicePurchaseOrderNumber}
                          {p.paidWithPaymentId ? ' · paid with its order' : ''}
                        </div>
                      ) : null}
                      {p.note ? <div className="text-2xs">{p.note}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {p.method ? OUTGOING_PAYMENT_METHOD_LABELS[p.method] : '—'}
                      {p.reference ? <div className="text-2xs">{p.reference}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-end tabular font-medium">
                      {formatMoney(p.amountCents)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={STATUS_TONE[p.status]}>{humanizeStatus(p.status)}</Badge>
                      {p.voidReason ? (
                        <div className="mt-0.5 text-2xs text-foreground-muted">{p.voidReason}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {p.document ? (
                          <a
                            href={`${apiBaseUrl}/outgoing-payments/${p.id}/document`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <Paperclip className="size-3.5" aria-hidden="true" /> Receipt
                          </a>
                        ) : canManage && p.status !== 'void' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setAttachFor(p.id)
                              fileRef.current?.click()
                            }}
                          >
                            <Paperclip /> Attach
                          </Button>
                        ) : null}
                        {canManage && p.status !== 'void' ? (
                          <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                            <Pencil /> Edit
                          </Button>
                        ) : null}
                        {canManage && p.status === 'unpaid' ? (
                          <Button variant="secondary" size="sm" onClick={() => setPaying(p)}>
                            Mark paid
                          </Button>
                        ) : null}
                        {canManage && p.status !== 'void' ? (
                          <Button variant="ghost" size="sm" onClick={() => setVoiding(p)}>
                            Void
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={Wallet}
                      title="No payments here yet"
                      description="Record a payment, or change the period and filters."
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <InfiniteScrollFooter
          total={total}
          loaded={items.length}
          hasMore={Boolean(hasNextPage)}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
        />
      </div>

      <EditPaymentDialog payment={editing} onClose={() => setEditing(null)} />
      <PayBillDialog payment={paying} onClose={() => setPaying(null)} />
      <VoidDialog payment={voiding} onClose={() => setVoiding(null)} />
    </div>
  )
}

function EditPaymentDialog({
  payment,
  onClose,
}: {
  payment: OutgoingPayment | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [method, setMethod] = useState<OutgoingPaymentMethod>('ach')
  const [paidOn, setPaidOn] = useState('')
  const [dueOn, setDueOn] = useState('')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const paid = payment?.status === 'paid'
  // `stock` is written by the system when inventory is used in the office; it is not pickable.
  const methodEditable = paid && payment?.method !== 'stock'

  useEffect(() => {
    if (!payment) return
    setFormError(null)
    setMethod(payment.method ?? 'ach')
    setPaidOn(payment.paidOn ?? '')
    setDueOn(payment.dueOn ?? '')
    setReference(payment.reference ?? '')
    setNote(payment.note ?? '')
  }, [payment])

  // Only what changed is sent, so the locked fields (amount, payee, linked purchase) never go along.
  const changes: Record<string, string | null> = {}
  if (payment) {
    if ((reference.trim() || null) !== payment.reference)
      changes.reference = reference.trim() || null
    if ((note.trim() || null) !== payment.note) changes.note = note.trim() || null
    if (methodEditable && method !== payment.method) changes.method = method
    if (paid && paidOn && paidOn !== payment.paidOn) changes.paidOn = paidOn
    if (!paid && (dueOn || null) !== payment.dueOn) changes.dueOn = dueOn || null
  }
  const invalid = paid && !paidOn

  const save = useMutation({
    mutationFn: () => api.patch(`/outgoing-payments/${payment?.id}`, changes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() })
      toast.success('Payment updated')
      onClose()
    },
    onError: (error: Error) => {
      const message = error instanceof ApiError ? error.message : 'Something went wrong'
      setFormError(message)
      toast.error('Could not save the payment', message)
    },
  })

  return (
    <Dialog open={Boolean(payment)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Edit {payment?.number} — {payment?.recipient}{' '}
            {payment ? formatMoney(payment.amountCents) : ''}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            The amount, who it was paid to and any linked purchase can’t be changed here — void it
            and record a new one to change those.
          </p>
          <Field label="Reference" hint="e.g. a Zelle confirmation number">
            {(props) => (
              <Input {...props} value={reference} onChange={(e) => setReference(e.target.value)} />
            )}
          </Field>
          {paid ? (
            <div className="grid grid-cols-2 gap-3">
              {methodEditable ? (
                <Field label="Method" required>
                  {(props) => (
                    <Select
                      value={method}
                      onValueChange={(v) => setMethod(v as OutgoingPaymentMethod)}
                    >
                      <SelectTrigger {...props}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MANUAL_PAYMENT_METHODS.map((m) => (
                          <SelectItem key={m} value={m}>
                            {OUTGOING_PAYMENT_METHOD_LABELS[m]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}
              <Field label="Date paid" required>
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={paidOn}
                    onChange={(e) => setPaidOn(e.target.value)}
                  />
                )}
              </Field>
            </div>
          ) : (
            <Field label="Due date">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={dueOn}
                  onChange={(e) => setDueOn(e.target.value)}
                />
              )}
            </Field>
          )}
          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          {formError ? (
            <p role="alert" className="text-sm text-danger">
              {formError}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={invalid || Object.keys(changes).length === 0}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PayBillDialog({
  payment,
  onClose,
}: {
  payment: OutgoingPayment | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [method, setMethod] = useState<OutgoingPaymentMethod>('ach')
  const [paidOn, setPaidOn] = useState(iso(new Date()))
  const [reference, setReference] = useState('')
  const [markPaid, setMarkPaid] = useState(false)
  const linkable = Boolean(payment?.purchaseOrderId || payment?.invoiceRef)

  useEffect(() => {
    if (!payment) return
    setMethod('ach')
    setPaidOn(iso(new Date()))
    setReference('')
    setMarkPaid(linkable)
  }, [payment, linkable])

  const pay = useMutation({
    mutationFn: () =>
      api.post(`/outgoing-payments/${payment?.id}/pay`, {
        method,
        paidOn,
        reference: reference.trim() || null,
        markOrderPaid: linkable && markPaid,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() })
      toast.success('Marked paid')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not mark it paid', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={Boolean(payment)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Mark paid — {payment?.recipient} {payment ? formatMoney(payment.amountCents) : ''}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Method" required>
              {(props) => (
                <Select value={method} onValueChange={(v) => setMethod(v as OutgoingPaymentMethod)}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MANUAL_PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {OUTGOING_PAYMENT_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Date paid" required>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={paidOn}
                  onChange={(e) => setPaidOn(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field label="Reference">
            {(props) => (
              <Input {...props} value={reference} onChange={(e) => setReference(e.target.value)} />
            )}
          </Field>
          {linkable ? (
            <div className="flex items-center gap-2 text-sm">
              <Switch
                checked={markPaid}
                onCheckedChange={setMarkPaid}
                aria-label="Mark the linked purchase paid"
              />
              Mark the linked purchase paid too
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={pay.isPending} onClick={() => pay.mutate()}>
            Mark paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function VoidDialog({
  payment,
  onClose,
}: {
  payment: OutgoingPayment | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  useEffect(() => {
    if (payment) setReason('')
  }, [payment])

  const doVoid = useMutation({
    mutationFn: () => api.post(`/outgoing-payments/${payment?.id}/void`, { reason }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() })
      toast.success('Payment voided')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not void it', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={Boolean(payment)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Void {payment?.number}?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground-muted">
            It stays in the history but no longer counts toward totals.
            {payment?.purchaseOrderId || payment?.invoiceRef
              ? ' If it marked a purchase paid, that goes back to unpaid unless another payment covers it.'
              : ''}
          </p>
          <Field label="Reason" required>
            {(props) => (
              <Input {...props} value={reason} onChange={(e) => setReason(e.target.value)} />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={doVoid.isPending}
            disabled={!reason.trim()}
            onClick={() => doVoid.mutate()}
          >
            Void payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ------------------------------------------------------------------- people

function PeopleTab({
  summary,
  canManage,
  onHistory,
}: {
  summary: OutgoingPaymentSummary | undefined
  canManage: boolean
  onHistory: (p: Person) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Payee | 'new' | null>(null)
  const { data } = useQuery({
    queryKey: queryKeys.expenses.payees(),
    queryFn: () => api.get<{ items: Payee[] }>('/payees'),
  })
  const payees = data?.items ?? []

  const toggle = useMutation({
    mutationFn: (p: { id: string; isActive: boolean }) =>
      api.patch(`/payees/${p.id}`, { isActive: p.isActive }),
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.expenses.payees() }),
  })

  const paidById = new Map((summary?.byPerson ?? []).map((p) => [p.id, p]))

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className="text-sm font-medium">Paid in this period</h3>
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className={th}>Name</th>
                <th className={th}>Type</th>
                <th className={`${th} text-end`}>Payments</th>
                <th className={`${th} text-end`}>Paid</th>
                <th className={`${th} text-end`}>Still to pay</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {(summary?.byPerson ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-foreground-muted">
                    No payments to staff, freelancers or vendors in this period.
                  </td>
                </tr>
              ) : (
                (summary?.byPerson ?? []).map((p) => (
                  <tr key={p.key} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{p.label}</td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {OUTGOING_PAYMENT_KIND_LABELS[p.kind]}
                    </td>
                    <td className="px-3 py-2 text-end tabular">{p.count}</td>
                    <td className="px-3 py-2 text-end tabular font-medium">
                      {formatMoney(p.paidCents)}
                    </td>
                    <td className="px-3 py-2 text-end tabular">
                      {formatMoney(p.outstandingCents)}
                    </td>
                    <td className="px-3 py-2 text-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onHistory({ kind: p.kind, id: p.id, label: p.label })}
                      >
                        History
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Freelancers & contractors</h3>
          {canManage ? (
            <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
              <Plus /> Add freelancer
            </Button>
          ) : null}
        </div>
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className={th}>Name</th>
                <th className={th}>Zelle / email</th>
                <th className={th}>Usually paid by</th>
                <th className={`${th} text-end`}>Paid this period</th>
                <th className={th}>Active</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {payees.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      icon={Wallet}
                      title="No freelancers yet"
                      description="Add the freelancers and contractors you pay so you can log what each one is paid."
                    />
                  </td>
                </tr>
              ) : (
                payees.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {p.zelleHandle || p.email || '—'}
                      {p.zelleHandle ? (
                        <button
                          type="button"
                          aria-label={`Copy ${p.zelleHandle}`}
                          className="ms-1.5 align-middle text-foreground-muted hover:text-foreground"
                          onClick={() => {
                            void navigator.clipboard?.writeText(p.zelleHandle ?? '')
                            toast.success('Copied')
                          }}
                        >
                          <Copy className="size-3.5" />
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {p.defaultMethod ? OUTGOING_PAYMENT_METHOD_LABELS[p.defaultMethod] : '—'}
                    </td>
                    <td className="px-3 py-2 text-end tabular">
                      {formatMoney(paidById.get(p.id)?.paidCents ?? 0)}
                    </td>
                    <td className="px-3 py-2">
                      <Switch
                        checked={p.isActive}
                        disabled={!canManage}
                        onCheckedChange={(checked) =>
                          toggle.mutate({ id: p.id, isActive: checked })
                        }
                        aria-label={`${p.isActive ? 'Deactivate' : 'Activate'} ${p.name}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-end">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onHistory({ kind: 'payee', id: p.id, label: p.name })}
                        >
                          History
                        </Button>
                        {canManage ? (
                          <Button variant="secondary" size="sm" onClick={() => setEditing(p)}>
                            Edit
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <PayeeDialog target={editing} onClose={() => setEditing(null)} />
    </div>
  )
}

function PayeeDialog({ target, onClose }: { target: Payee | 'new' | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const payee = target && target !== 'new' ? target : null
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [zelle, setZelle] = useState('')
  const [method, setMethod] = useState<string>(ALL)
  const [notes, setNotes] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!target) return
    setFormError(null)
    setName(payee?.name ?? '')
    setEmail(payee?.email ?? '')
    setPhone(payee?.phone ?? '')
    setZelle(payee?.zelleHandle ?? '')
    setMethod(payee?.defaultMethod ?? ALL)
    setNotes(payee?.notes ?? '')
  }, [target, payee])

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        email: email.trim() || null,
        phone: phone.trim() || null,
        zelleHandle: zelle.trim() || null,
        defaultMethod: method === ALL ? null : method,
        notes: notes.trim() || null,
      }
      const parsed = createPayeeSchema.safeParse(body)
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? 'Invalid freelancer')
      return payee
        ? api.patch(`/payees/${payee.id}`, parsed.data)
        : api.post('/payees', parsed.data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.expenses.payees() })
      toast.success(payee ? 'Saved' : 'Freelancer added')
      onClose()
    },
    onError: (error: Error) => {
      setFormError(error.message)
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined)
    },
  })

  return (
    <Dialog open={Boolean(target)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{payee ? `Edit ${payee.name}` : 'Add a freelancer'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label="Phone">
              {(props) => (
                <Input {...props} value={phone} onChange={(e) => setPhone(e.target.value)} />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Zelle email or phone">
              {(props) => (
                <Input {...props} value={zelle} onChange={(e) => setZelle(e.target.value)} />
              )}
            </Field>
            <Field label="Usually paid by">
              {(props) => (
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>No default</SelectItem>
                    {MANUAL_PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {OUTGOING_PAYMENT_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <Field label="Notes">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </Field>
          {formError ? (
            <p role="alert" className="text-sm text-danger">
              {formError}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!name.trim()}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------- recurring

function RecurringTab({ canManage }: { canManage: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<RecurringExpense | 'new' | null>(null)
  const { data } = useQuery({
    queryKey: queryKeys.expenses.recurring(),
    queryFn: () => api.get<{ items: RecurringExpense[] }>('/recurring-expenses'),
  })
  const items = data?.items ?? []

  const toggle = useMutation({
    mutationFn: (r: { id: string; isActive: boolean }) =>
      api.patch(`/recurring-expenses/${r.id}`, { isActive: r.isActive }),
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.expenses.recurring() }),
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-foreground-muted">
          A monthly bill, like rent, shows up here as an unpaid expense on its day each month — mark
          it paid when you pay it.
        </p>
        {canManage ? (
          <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
            <Plus /> New recurring expense
          </Button>
        ) : null}
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-sunken/60">
              <th className={th}>What</th>
              <th className={th}>Paid to</th>
              <th className={th}>Category</th>
              <th className={th}>Day</th>
              <th className={`${th} text-end`}>Amount</th>
              <th className={th}>Active</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={Wallet}
                    title="Nothing recurring yet"
                    description="Add rent or any other monthly bill."
                  />
                </td>
              </tr>
            ) : (
              items.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 font-medium">{r.label}</td>
                  <td className="px-3 py-2 text-foreground-muted">{r.recipient}</td>
                  <td className="px-3 py-2 text-foreground-muted">{r.categoryName}</td>
                  <td className="px-3 py-2 tabular">Day {r.dayOfMonth}</td>
                  <td className="px-3 py-2 text-end tabular font-medium">
                    {formatMoney(r.amountCents)}
                  </td>
                  <td className="px-3 py-2">
                    <Switch
                      checked={r.isActive}
                      disabled={!canManage}
                      onCheckedChange={(checked) => toggle.mutate({ id: r.id, isActive: checked })}
                      aria-label={`${r.isActive ? 'Pause' : 'Resume'} ${r.label}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-end">
                    {canManage ? (
                      <Button variant="secondary" size="sm" onClick={() => setEditing(r)}>
                        Edit
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <RecurringDialog target={editing} onClose={() => setEditing(null)} />
    </div>
  )
}

function RecurringDialog({
  target,
  onClose,
}: {
  target: RecurringExpense | 'new' | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const lookups = usePaymentLookups(Boolean(target))
  const existing = target && target !== 'new' ? target : null
  const [label, setLabel] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [payeeLabel, setPayeeLabel] = useState('')
  const [amount, setAmount] = useState('')
  const [day, setDay] = useState('1')
  const [startsOn, setStartsOn] = useState(iso(new Date()))
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!target) return
    setFormError(null)
    setLabel(existing?.label ?? '')
    setCategoryId(existing?.categoryId ?? '')
    setPayeeLabel(existing?.payeeLabel ?? '')
    setAmount(existing ? (existing.amountCents / 100).toString() : '')
    setDay(String(existing?.dayOfMonth ?? 1))
    setStartsOn(existing?.startsOn ?? iso(new Date()))
  }, [target, existing])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: label.trim(),
        categoryId,
        payeeLabel: payeeLabel.trim() || null,
        amountCents: Math.round(Number(amount || 0) * 100),
        dayOfMonth: Number(day),
        startsOn,
      }
      return existing
        ? api.patch(`/recurring-expenses/${existing.id}`, body)
        : api.post('/recurring-expenses', body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.expenses.recurring() })
      toast.success(existing ? 'Saved' : 'Recurring expense added')
      onClose()
    },
    onError: (error: Error) => {
      setFormError(error instanceof ApiError ? error.message : error.message)
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined)
    },
  })

  return (
    <Dialog open={Boolean(target)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.label}` : 'New recurring expense'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="What is it" required hint="e.g. Monthly rent">
            {(props) => (
              <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <Field label="Category" required>
            {(props) => (
              <Select value={categoryId || undefined} onValueChange={setCategoryId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {lookups.categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Paid to" required hint="e.g. Landlord">
            {(props) => (
              <Input
                {...props}
                value={payeeLabel}
                onChange={(e) => setPayeeLabel(e.target.value)}
              />
            )}
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Amount" required hint="In dollars.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
            </Field>
            <Field label="Day of month" required hint="1–28">
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                />
              )}
            </Field>
            <Field label="Starts" required>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={startsOn}
                  onChange={(e) => setStartsOn(e.target.value)}
                />
              )}
            </Field>
          </div>
          {formError ? (
            <p role="alert" className="text-sm text-danger">
              {formError}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!label.trim() || !categoryId || !payeeLabel.trim() || Number(amount) <= 0}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --------------------------------------------------------------- categories

interface CategoryRow {
  id: string
  name: string
  isActive: boolean
}

function CategoriesTab({ canManage }: { canManage: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const { data } = useQuery({
    queryKey: queryKeys.expenses.categories(),
    queryFn: () => api.get<{ items: CategoryRow[] }>('/expense-categories'),
  })
  const items = data?.items ?? []
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.expenses.categories() })
  const fail = (error: unknown) =>
    toast.error('Could not save', error instanceof ApiError ? error.message : undefined)

  const add = useMutation({
    mutationFn: () => api.post('/expense-categories', { name: name.trim() }),
    onSuccess: () => {
      setName('')
      return refresh()
    },
    onError: fail,
  })
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/expense-categories/${v.id}`, v.body),
    onSuccess: () => {
      setRenaming(null)
      return refresh()
    },
    onError: fail,
  })

  return (
    <div className="max-w-xl space-y-3">
      {canManage ? (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) add.mutate()
          }}
        >
          <Input
            aria-label="New category"
            placeholder="New category, e.g. Insurance"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" variant="secondary" loading={add.isPending} disabled={!name.trim()}>
            <Plus /> Add
          </Button>
        </form>
      ) : null}
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        {items.map((c) => (
          <div
            key={c.id}
            className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-0"
          >
            {renaming === c.id ? (
              <form
                className="flex flex-1 gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (draft.trim()) patch.mutate({ id: c.id, body: { name: draft.trim() } })
                }}
              >
                <Input
                  aria-label="Category name"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <Button type="submit" size="sm" variant="primary">
                  Save
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(null)}>
                  Cancel
                </Button>
              </form>
            ) : (
              <>
                <span
                  className={`flex-1 text-sm ${c.isActive ? '' : 'text-foreground-muted line-through'}`}
                >
                  {c.name}
                </span>
                {canManage ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRenaming(c.id)
                        setDraft(c.name)
                      }}
                    >
                      Rename
                    </Button>
                    <Switch
                      checked={c.isActive}
                      onCheckedChange={(checked) =>
                        patch.mutate({ id: c.id, body: { isActive: checked } })
                      }
                      aria-label={`${c.isActive ? 'Deactivate' : 'Activate'} ${c.name}`}
                    />
                  </>
                ) : null}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
